import { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { GetRequestTenant } from "../../../services/tenantrequest";
import { GetSafetyTask } from "../../../services/safetytask";
import { GetWorkPermit, WorkPermitHeaderListData, WorkPermitData } from "../../../services/workPermitService";
import { subscribeTenantChange } from "../../../utils/tenantChangeEvent";

interface Activity {
  id: string;
  date: string;
  time: string;
  type: "Safety Task" | "Work Permit" | "Tenant Request";
  message: string;
  status: string;
  timestamp: number;
}

interface TenantRequestData {
  TenantRequestId: number;
  CreatedOn?: string;
  UpdatedOn?: string;
  Notes?: string;
  RequestNumber?: string;
  TenantRequestName?: string;
  RequestStatusName?: string;
}

interface SafetyTaskData {
  SafetyTaskId: number;
  CreatedOn?: string;
  UpdatedOn?: string;
  Notes?: string;
  SafetyTaskNumber?: string;
  Description?: string;
  SafetyStatusName?: string;
  SafetyStatusId?: number;
}

type FilterType = "All" | "Work Permit" | "Safety Task" | "Tenant Request";

const TYPE_COLORS: Record<string, { bg: string; text: string; dot: string }> = {
  "Work Permit": {
    bg: "bg-blue-50 dark:bg-blue-900/30",
    text: "text-blue-600 dark:text-blue-400",
    dot: "bg-blue-500",
  },
  "Safety Task": {
    bg: "bg-amber-50 dark:bg-amber-900/30",
    text: "text-amber-600 dark:text-amber-400",
    dot: "bg-amber-500",
  },
  "Tenant Request": {
    bg: "bg-green-50 dark:bg-green-900/30",
    text: "text-green-600 dark:text-green-400",
    dot: "bg-green-500",
  },
};

export default function RecentActivitySection() {
  const navigate = useNavigate();
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState<FilterType>("All");
  const isFetchingRef = useRef(false);

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return { date: "Invalid Date", time: "00:00", timestamp: 0 };
      }
      return {
        date: date.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        }),
        time: date.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        }),
        timestamp: date.getTime(),
      };
    } catch {
      return { date: "Invalid Date", time: "00:00", timestamp: 0 };
    }
  };

  const scrollToSection = (type: string) => {
    const map: Record<string, string> = {
      "Work Permit": "work-permit-section",
      "Safety Task": "safety-task-section",
      "Tenant Request": "tenant-request-section",
    };
    const sectionId = map[type];
    if (!sectionId) return;
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
      element.classList.add("ring-2", "ring-blue-500", "ring-offset-2");
      setTimeout(
        () => element.classList.remove("ring-2", "ring-blue-500", "ring-offset-2"),
        2000
      );
    }
  };

  const handleItemClick = (activity: Activity) => {
    if (activity.type === "Safety Task") {
      const numericId = activity.id.replace("st-", "");
      if (numericId && !isNaN(Number(numericId))) {
        navigate(`/safetyTask/form/${numericId}`);
        return;
      }
      navigate("/safetyTask");
    } else if (activity.type === "Tenant Request") {
      navigate("/tenantRequest");
    } else if (activity.type === "Work Permit") {
      const parts = activity.id.replace("wp-", "").split("-");
      const wpId = Number(parts[0]);
      const permitTypeId = Number(parts[1]);
      if (wpId > 0) {
        navigate(`/workpermit/form/${wpId}`, {
          state: { viewMode: true, initialPermitTypeId: permitTypeId }
        });
        return;
      }
      navigate("/workPermit");
    } else {
      scrollToSection(activity.type);
    }
  };

  const handleViewAll = () => {
    if (activeFilter === "Safety Task") {
      navigate("/safetyTask");
    } else if (activeFilter === "Tenant Request") {
      navigate("/tenantRequest");
    } else if (activeFilter === "Work Permit") {
      navigate("/workPermit");
    } else {
      const element = document.getElementById("safety-task-section");
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
        element.classList.add("ring-2", "ring-blue-500", "ring-offset-2");
        setTimeout(
          () => element.classList.remove("ring-2", "ring-blue-500", "ring-offset-2"),
          2000
        );
      } else {
        navigate("/safetyTask");
      }
    }
  };

  const fetchAllActivities = async () => {
    // Cegah double fetch
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    try {
      // Selalu baca dari storage agar dapat nilai terbaru saat dipanggil dari event
      const tenantId = localStorage.getItem("tenant_id") || sessionStorage.getItem("tenant_id");
      const mallId = localStorage.getItem("mall_id") || sessionStorage.getItem("mall_id");

      const currentTenant = tenantId ? parseInt(tenantId) : 0;
      const currentMall = mallId ? parseInt(mallId) : 0;

      if (!currentMall || currentMall === 0) {
        setActivities([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      const allActivities: Activity[] = [];

      try {
        const tenantRequestRes = await GetRequestTenant(
          currentTenant,
          0,
          currentMall,
          0,
          0,
          0,
          0
        );
        if (
          tenantRequestRes?.requestTenantDatas &&
          Array.isArray(tenantRequestRes.requestTenantDatas)
        ) {
          tenantRequestRes.requestTenantDatas.forEach((request: TenantRequestData) => {
            const dateInfo = formatDate(request.CreatedOn || request.UpdatedOn || "");
            if (dateInfo.timestamp > 0) {
              allActivities.push({
                id: `tr-${request.TenantRequestId}`,
                date: dateInfo.date,
                time: dateInfo.time,
                type: "Tenant Request",
                message: request.Notes || request.RequestNumber || "Tenant request activity",
                status: request.RequestStatusName || request.TenantRequestName || "New",
                timestamp: dateInfo.timestamp,
              });
            }
          });
        }
      } catch (error) {
        console.error("Error fetching tenant requests:", error);
      }

      try {
        const safetyTaskRes = await GetSafetyTask(0, currentTenant, 0, false);
        if (
          safetyTaskRes?.safetyTaskDatas &&
          Array.isArray(safetyTaskRes.safetyTaskDatas)
        ) {
          safetyTaskRes.safetyTaskDatas.forEach((task: SafetyTaskData) => {
            const dateInfo = formatDate(task.CreatedOn || task.UpdatedOn || "");
            if (dateInfo.timestamp > 0) {
              const statusName = task.SafetyStatusName || (
                task.SafetyStatusId === 1 ? "New" :
                  task.SafetyStatusId === 2 ? "In Progress" :
                    task.SafetyStatusId === 3 ? "Done" :
                      task.SafetyStatusId === 4 ? "Close" :
                        task.SafetyStatusId === 5 ? "Reject" : "New"
              );
              allActivities.push({
                id: `st-${task.SafetyTaskId}`,
                date: dateInfo.date,
                time: dateInfo.time,
                type: "Safety Task",
                message: task.Notes || task.SafetyTaskNumber || task.Description || "Safety task activity",
                status: statusName,
                timestamp: dateInfo.timestamp,
              });
            }
          });
        }
      } catch (error) {
        console.error("Error fetching safety tasks:", error);
      }

      try {
        const workPermitRes = await GetWorkPermit(0, 0, 0, currentMall, currentTenant, 0, 0);

        if (workPermitRes?.workPermitHeaderDatas && Array.isArray(workPermitRes.workPermitHeaderDatas)) {
          workPermitRes.workPermitHeaderDatas.forEach((header: WorkPermitHeaderListData) => {
            if (header.workPermitDatas && Array.isArray(header.workPermitDatas) && header.workPermitDatas.length > 0) {
              header.workPermitDatas.forEach((permit: WorkPermitData) => {
                const permitId = permit.WorkPermitId || header.WorkPermitHeaderId || 0;
                // Ambil PermitNumber langsung dari setiap item workPermitDatas — BUKAN dari header
                const permitNumber = permit.PermitNumber || "";

                let permitTypeName = permit.PermitTypeName || "";
                if (!permitTypeName) {
                  if (permit.PermitTypeId === 30) permitTypeName = "Surat Izin Kerja";
                  else if (permit.PermitTypeId === 10) permitTypeName = "Surat Izin Masuk Barang";
                  else if (permit.PermitTypeId === 20) permitTypeName = "Surat Izin Keluar Barang";
                }

                // Prioritaskan CreatedOn per-permit agar setiap item punya timestamp unik
                const dateStr = permit.CreatedOn || permit.UpdatedOn || (header as any).CreatedOn || (header as any).UpdatedOn || permit.StartOn || header.StartOn || "";
                const dateInfo = formatDate(dateStr);
                const timeStr = permit.CreatedOn
                  ? dateInfo.time
                  : (permit.StartTime || header.StartTime || dateInfo.time || "00:00");

                let statusDesc = permit.PermitStatusDesc || (header as any).PermitHeaderStatusDesc || "";
                if (!statusDesc) {
                  const statusVal = permit.PermitStatus ?? header.PermitHeaderStatus;
                  if (statusVal === 10) statusDesc = "Pending Approval";
                  else if (statusVal === 20) statusDesc = "Approved";
                  else if (statusVal === 30) statusDesc = "Reject";
                  else if (statusVal === 40) statusDesc = "Waiting";
                  else statusDesc = "Draft";
                }

                const displayMessage = permitNumber
                  ? (permitTypeName ? `${permitNumber} - ${permitTypeName}` : permitNumber)
                  : (permit.Description || header.Description || "Work permit activity");

                allActivities.push({
                  id: `wp-${permitId}-${permit.PermitTypeId || 0}`,
                  date: dateInfo.date !== "Invalid Date" ? dateInfo.date : "Today",
                  time: timeStr,
                  type: "Work Permit",
                  message: displayMessage,
                  status: statusDesc,
                  // Kalikan permitId supaya setiap permit memiliki timestamp unik meski StartOn sama
                  timestamp: (dateInfo.timestamp > 0 ? dateInfo.timestamp : 0) + (Number(permitId) * 1000),
                });
              });
            }
          });
        }
      } catch (error) {
        console.error("Error fetching work permits:", error);
      }

      allActivities.sort((a, b) => b.timestamp - a.timestamp);
      setActivities(allActivities);
    } catch (error) {
      console.error("Error fetching activities:", error);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  };

  // Initial fetch saat pertama load
  useEffect(() => {
    fetchAllActivities();
  }, []);

  // Fetch ulang saat tenant berubah via dropdown
  useEffect(() => {
    const handleTenantChange = () => {
      fetchAllActivities();
    };

    const unsubscribe = subscribeTenantChange(handleTenantChange);
    return unsubscribe;
  }, []);

  const filteredActivities = useMemo(() => {
    if (activeFilter === "All") return activities.slice(0, 8);
    return activities.filter((a) => a.type === activeFilter).slice(0, 8);
  }, [activities, activeFilter]);

  const filterButtons: FilterType[] = [
    "All",
    "Work Permit",
    "Safety Task",
    "Tenant Request",
  ];

  return (
    <div className="rounded-2xl bg-white p-4 sm:p-6 shadow-sm border border-gray-100 dark:bg-gray-900 dark:border-gray-700 h-full flex flex-col">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Recent Activity
        </h3>
        <div className="flex items-center">
          <select
            value={activeFilter}
            onChange={(e) => setActiveFilter(e.target.value as FilterType)}
            className="px-3 py-1.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-800 dark:text-gray-200 text-xs sm:text-sm font-medium outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer shadow-sm"
          >
            {filterButtons.map((filter) => (
              <option key={filter} value={filter}>
                {filter}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-4 border-t pt-4 space-y-3 max-h-[350px] overflow-y-auto pr-1">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        ) : filteredActivities.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            No recent activities found
          </div>
        ) : (
          filteredActivities.map((a) => {
            const colors = TYPE_COLORS[a.type] ?? TYPE_COLORS["Work Permit"];
            return (
              <div
                key={a.id}
                onClick={() => handleItemClick(a)}
                className="cursor-pointer hover:bg-slate-50 dark:hover:bg-gray-800/60 p-2 rounded-xl transition-colors"
              >
                <div className="sm:hidden flex items-start justify-between gap-2 py-1">
                  <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                    <span
                      className={`inline-flex items-center self-start px-2 py-0.5 rounded-md text-xs font-semibold ${colors.bg} ${colors.text}`}
                    >
                      {a.type}
                    </span>
                    <p className="text-sm text-gray-700 dark:text-gray-200 break-all leading-snug">
                      {a.message}
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500">
                      {a.date} · {a.time}
                    </p>
                  </div>
                </div>

                <div className="hidden sm:flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    <div className="text-xs text-gray-400 w-20 flex-shrink-0">
                      <div className="font-medium text-gray-700 dark:text-gray-300">
                        {a.date}
                      </div>
                      <div className="text-xs">{a.time}</div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-3">
                        <div className="text-sm text-blue-600 font-medium flex-shrink-0">
                          {a.type}
                        </div>
                        <div className="text-sm text-gray-700 dark:text-gray-200 truncate">
                          {a.message}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="mt-3 text-right">
        <button
          type="button"
          onClick={handleViewAll}
          className="text-sm text-blue-600 dark:text-blue-400 hover:underline cursor-pointer font-medium"
        >
          View all activity
        </button>
      </div>
    </div>
  );
}